import { differenceInMonths, differenceInDays } from "date-fns";

/**
 * Calculate GPS tracker fees based on issue duration
 * @param {Array} gpsTrackers - Array of GPS tracker objects
 * @param {number} monthlyFeePerTracker - Monthly fee per GPS tracker (default: 400)
 * @returns {Object} GPS fee calculation details
 */
export const calculateGpsFees = (gpsTrackers, monthlyFeePerTracker = 400) => {
  if (!gpsTrackers || !Array.isArray(gpsTrackers) || gpsTrackers.length === 0) {
    return {
      totalFee: 0,
      trackerFees: [],
      summary: {
        totalTrackers: 0,
        totalMonthsCharged: 0,
        averageMonthsPerTracker: 0
      }
    };
  }

  const currentDate = new Date();
  let totalFee = 0;
  let totalMonthsCharged = 0;
  const trackerFees = [];

  gpsTrackers.forEach((tracker) => {
    let monthsCharged = 0;
    let fee = 0;
    let issueDate = null;

    // Handle different possible field names for the fee
    const trackerMonthlyFee = tracker.gps_fee || tracker.monthly_fee || monthlyFeePerTracker;
    
    // Debug logging to help troubleshoot
    if (process.env.NODE_ENV === 'development') {
      console.log(`GPS Tracker ${tracker.gps_code}: gps_fee=${tracker.gps_fee}, using=${trackerMonthlyFee}`);
    }

    if (tracker.date_issued) {
      issueDate = new Date(tracker.date_issued);
      
      // Calculate months between issue date and current date
      monthsCharged = differenceInMonths(currentDate, issueDate);
      
      // If less than a month but more than 0 days, charge for at least 1 month
      if (monthsCharged === 0) {
        const daysIssued = differenceInDays(currentDate, issueDate);
        if (daysIssued > 0) {
          monthsCharged = 1;
        }
      }

      // Ensure minimum of 1 month if GPS is issued
      if (monthsCharged < 1 && issueDate <= currentDate) {
        monthsCharged = 1;
      }

      fee = monthsCharged * trackerMonthlyFee;
      totalFee += fee;
      totalMonthsCharged += monthsCharged;
    }

    trackerFees.push({
      trackerId: tracker.id,
      gpsCode: tracker.gps_code,
      serialNumber: tracker.serial_number,
      dateIssued: issueDate,
      monthsCharged,
      monthlyFee: trackerMonthlyFee,
      totalFee: fee,
      status: tracker.status,
      daysIssued: issueDate ? differenceInDays(currentDate, issueDate) : 0
    });
  });

  return {
    totalFee: Number(totalFee.toFixed(2)),
    trackerFees,
    summary: {
      totalTrackers: gpsTrackers.length,
      totalMonthsCharged,
      averageMonthsPerTracker: gpsTrackers.length > 0 ? Number((totalMonthsCharged / gpsTrackers.length).toFixed(2)) : 0,
      monthlyFeeRate: monthlyFeePerTracker
    }
  };
};

/**
 * Calculate GPS fees for a specific loan by fetching tracker data
 * @param {string|number} loanId - Loan ID
 * @param {string} token - Authorization token
 * @param {string|number} companyId - Company ID
 * @param {number} monthlyFeePerTracker - Monthly fee per GPS tracker
 * @returns {Promise<Object>} GPS fee calculation details
 */
export const calculateLoanGpsFees = async (loanId, token, companyId, monthlyFeePerTracker = 400) => {
  try {
    const response = await fetch(
      `https://zedlenders.pickmesms.com/items/gps_trackers?filter[assigned_loan]=${loanId}&filter[company]=${companyId}&fields=*`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch GPS trackers: ${response.statusText}`);
    }

    const data = await response.json();
    const gpsTrackers = data.data || [];

    return calculateGpsFees(gpsTrackers, monthlyFeePerTracker);
  } catch (error) {
    console.error("Error calculating loan GPS fees:", error);
    return {
      totalFee: 0,
      trackerFees: [],
      summary: {
        totalTrackers: 0,
        totalMonthsCharged: 0,
        averageMonthsPerTracker: 0
      },
      error: error.message
    };
  }
};

/**
 * Get GPS fee breakdown for display purposes
 * @param {Object} gpsFeeData - Result from calculateGpsFees
 * @returns {Array} Formatted breakdown for display
 */
export const getGpsFeeBreakdown = (gpsFeeData) => {
  if (!gpsFeeData || !gpsFeeData.trackerFees) {
    return [];
  }

  return gpsFeeData.trackerFees.map((tracker, index) => ({
    key: tracker.trackerId || index,
    gpsCode: tracker.gpsCode,
    serialNumber: tracker.serialNumber,
    dateIssued: tracker.dateIssued ? tracker.dateIssued.toLocaleDateString() : 'Not issued',
    daysIssued: tracker.daysIssued,
    monthsCharged: tracker.monthsCharged,
    monthlyFee: `K${tracker.monthlyFee.toLocaleString()}`,
    totalFee: `K${tracker.totalFee.toLocaleString()}`,
    status: tracker.status,
  }));
};

/**
 * Update loan total amount to include GPS fees
 * @param {number} loanAmount - Original loan amount
 * @param {number} gpsFees - Total GPS fees
 * @returns {number} Updated total amount owing
 */
export const calculateTotalAmountWithGpsFees = (loanAmount, gpsFees) => {
  return Number((Number(loanAmount) + Number(gpsFees)).toFixed(2));
};